/**
 * useAriaRun — transporte de la ejecución durable de ARIA (E2c).
 *
 * QUÉ RESUELVE. El chat actual vive dentro de una sola invocación: si la
 * conexión se corta, el turno se pierde entero y el usuario ve un error después
 * de haber esperado. Aquí la consulta es una entidad persistida (`agent_runs`)
 * que un worker ejecuta con 900 s de presupuesto, y el cliente sólo la OBSERVA.
 * Cortar la conexión deja de costar nada: cada evento tiene un `seq`, el cliente
 * guarda el último que vio y vuelve a pedir desde ahí.
 *
 * POR QUÉ SONDEO Y NO SSE. El endpoint va por API Gateway, que corta a los 29 s
 * — el mismo techo que la ejecución durable viene a esquivar; un SSE moriría
 * igual. El backend bloquea ~20 s por petición y responde en cuanto hay algo que
 * contar, así que en la práctica se comporta como un stream, pero uno cuya
 * desconexión es un no-evento. Ver `verix-api/src/routes/aria/runs.ts`.
 *
 * UN SOLO INTÉRPRETE. Los eventos que llegan aquí son los MISMOS objetos que
 * emite el SSE: el worker persiste el evento del grafo tal cual. Por eso este
 * hook no parsea nada — delega en `createAIEventSink`, compartido con
 * `useAIStream`. Un segundo intérprete sería repetir en el cliente la
 * divergencia entre pipelines que la auditoría documentó en el backend.
 *
 * Contrato — deliberadamente igual al de `useAIStream`, `params` incluidos, para
 * que un componente pueda cambiar de transporte sin tocar nada más:
 *
 *   const { run, abort, isRunning } = useAriaRun();
 *   await run({ message, params, analyticsContext, callbacks });
 *
 * `params` viaja entero al backend, que lo persiste como contexto del run. Es lo
 * que permite que el worker ejecute con la MISMA información que el chat
 * (adjuntos, menciones, escaneos OCR, Excel/Word, override). Si se quedara
 * fuera, el run no fallaría: respondería peor, en silencio.
 */

import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { track } from '@/lib/analytics';
import { captureLog } from '@/lib/errorReporter';
import { createAIEventSink } from './aiEventSink';
import { openPlanLimit } from '@/stores/planLimitStore';

const __LOG_CTX = { context: 'src/components/ai/hooks/useAriaRun' };

/**
 * Techo de observación. Por encima de los 20 min a los que el backend da un run
 * por obsoleto, a propósito: así el que corta siempre es el servidor y el
 * usuario lee un error real en vez de un "se acabó la paciencia del navegador".
 */
const MAX_WATCH_MS = 21 * 60 * 1000;
/** El backend bloquea ~20 s; 30 s deja margen de red sin colgar el sondeo. */
const POLL_TIMEOUT_MS = 30_000;
/**
 * Suelo entre sondeos. El bucle se apoya en que el servidor bloquea ~20 s, pero
 * apoyarse en eso a secas es frágil: si algo del camino responde al instante
 * (un proxy que no sostiene la conexión, el servidor cerrando antes de tiempo),
 * el bucle gira sin pausa y el cliente se convierte en una tormenta de
 * peticiones contra su propia API. El suelo sólo actúa cuando la respuesta
 * llegó antes de tiempo, así que no añade latencia al caso normal.
 */
const MIN_POLL_GAP_MS = 500;
/** Fallos de red seguidos que se toleran antes de rendirse. */
const MAX_CONSECUTIVE_FAILURES = 4;
const RETRY_BASE_MS = 800;
const SLOW_QUERY_TOAST_MS = 15_000;

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export default function useAriaRun() {
  const [isRunning, setIsRunning] = useState(false);
  // `stopped` y el controller viven juntos: abortar es marcar la bandera y
  // tumbar el sondeo en vuelo, sin esperar a que venza la ventana de 20 s.
  const ctlRef = useRef({ stopped: false, controller: null });

  const abort = useCallback(() => {
    ctlRef.current.stopped = true;
    try { ctlRef.current.controller?.abort(); } catch { /* ignore */ }
    ctlRef.current.controller = null;
    setIsRunning(false);
  }, []);

  const run = useCallback(async ({ message, params = {}, analyticsContext = {}, callbacks = {} }) => {
    const ctl = { stopped: false, controller: null };
    ctlRef.current = ctl;
    const isCurrent = () => ctlRef.current === ctl && !ctl.stopped;
    callbacks = Object.fromEntries(Object.entries(callbacks).map(([name, callback]) => [name, (...args) => isCurrent() ? callback?.(...args) : undefined]));
    const { onDone, onError, onHrOptInRequired } = callbacks;
    const startedAt = Date.now();
    setIsRunning(true);

    track('ai.message.sent', {
      ...analyticsContext,
      transport: 'run',
      message_len: typeof message === 'string' ? message.length : 0,
    });

    let slowToastId = null;
    const slowTimer = setTimeout(() => {
      if (!isCurrent()) return;
      slowToastId = toast.info(i18n.t('ai.stream.slow_query', 'La consulta está tardando más de lo habitual…'), {
        description: i18n.t('ai.stream.slow_query_desc', 'Seguimos procesando. Puedes cancelar con el botón si lo necesitas.'),
        duration: 20_000,
      });
    }, SLOW_QUERY_TOAST_MS);
    const clearSlowToast = () => {
      clearTimeout(slowTimer);
      if (slowToastId !== null) {
        try { toast.dismiss(slowToastId); } catch { /* best-effort */ }
        slowToastId = null;
      }
    };

    const fail = (payload, errorCode) => {
      if (!isCurrent()) return;
      clearSlowToast();
      track('ai.error', { ...analyticsContext, transport: 'run', duration_ms: Date.now() - startedAt, error_code: errorCode });
      onError?.(payload);
    };

    const { default: apiFetch } = await import('@/api/apiFetch');
    // `onPlanLimit` se inyecta aqui y no lo pide cada consumidor: el tope de plan
    // tiene que abrir el mismo modal SIEMPRE, igual que `query-client.ts` lo hace
    // para todas las mutaciones. Si el llamador trae el suyo, manda el suyo.
    const sink = createAIEventSink({ onPlanLimit: openPlanLimit, ...callbacks });

    try {
      if (!isCurrent()) return;
      // 1) Crear el run. A partir de aquí el trabajo es del worker: aunque el
      //    navegador se cierre, la ejecución sigue y sus eventos quedan grabados.
      // El cuerpo es el de `params` con dos renombres: la ruta REST llama
      // `question` al mensaje y `conversation_id` a la conversación. `prompt`,
      // `message` y `stream` son del transporte SSE y aquí no pintan nada; el
      // backend los descartaría igualmente (Zod `.strip()`), pero mandarlos
      // sería ruido. El resto —adjuntos, menciones, escaneos, override— viaja.
      const { conversationId, prompt: _p, message: _m, stream: _s, ...rest } = params;
      const created = await apiFetch('/api/aria/runs', {
        method: 'POST',
        body: JSON.stringify({
          ...rest,
          question: message,
          ...(conversationId ? { conversation_id: conversationId } : {}),
        }),
      });
      if (!isCurrent()) return;
      const runId = created?.run_id;
      if (!runId) throw new Error('El servidor no devolvió un identificador de ejecución.');

      // El backend crea la conversación al aceptar el run (si no venía una) y
      // devuelve su id aquí — el sink lo recibiría igualmente en el evento
      // `done`, pero fijarlo YA permite que el rail de sesiones liste la
      // conversación en curso y que un reload a mitad de turno la conserve.
      if (created.conversation_id) callbacks.onConversationId?.(created.conversation_id);

      let cursor = created.cursor ?? 0;
      let failures = 0;
      const deadline = Date.now() + MAX_WATCH_MS;

      // 2) Observar. El cursor es lo único que hay que conservar: un fallo de
      //    red reintenta con el MISMO valor, así que no se pierde ni se repite
      //    ningún evento.
      while (isCurrent() && Date.now() < deadline) {
        let page;
        const pollStartedAt = Date.now();
        try {
          const controller = new AbortController();
          ctl.controller = controller;
          const timer = setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
          try {
            page = await apiFetch(`/api/aria/runs/${runId}/events?after=${cursor}`, { signal: controller.signal });
          } finally {
            clearTimeout(timer);
            ctl.controller = null;
          }
          if (!isCurrent()) return;
          failures = 0;
        } catch (err) {
          if (!isCurrent()) break;
          // 404: el run no existe o no es de esta empresa. Reintentar no lo va
          // a hacer aparecer.
          if (err?.status === 404) {
            fail({ isTimeout: false, message: i18n.t('ai.run.not_found', 'La consulta ya no está disponible. Vuelve a preguntar.'), status: 404 }, 'run_not_found');
            return;
          }
          failures += 1;
          captureLog('warn', __LOG_CTX, '[useAriaRun] fallo al sondear', { run_id: runId, cursor, failures, error: err?.message });
          if (failures > MAX_CONSECUTIVE_FAILURES) {
            fail({ isTimeout: false, message: i18n.t('ai.run.lost_connection', 'Se perdió la conexión con la consulta. Vuelve a intentarlo.'), retryable: true }, 'poll_failed');
            return;
          }
          await delay(RETRY_BASE_MS * 2 ** (failures - 1));
          continue; // mismo cursor a propósito
        }

        for (const ev of page.events ?? []) {
          if (ev.type === 'hr_optin_required') {
            onHrOptInRequired?.();
            continue;
          }
          sink.apply(ev.payload);
        }
        if (page.events?.length) clearSlowToast();
        cursor = page.cursor ?? cursor;

        if (!page.done) {
          const elapsed = Date.now() - pollStartedAt;
          if (elapsed < MIN_POLL_GAP_MS) await delay(MIN_POLL_GAP_MS - elapsed);
          continue;
        }

        // 3) El run dejó de emitir. `done` lo decide el servidor: incluye los
        //    estados terminales y `awaiting_human` (el run sigue vivo pero
        //    espera al usuario — mantener el sondeo sólo daría un spinner).
        clearSlowToast();
        if (page.status === 'failed') {
          fail({ isTimeout: false, message: page.error || i18n.t('ai.run.failed', 'La consulta no pudo completarse. Vuelve a intentarlo.'), retryable: true }, 'run_failed');
          return;
        }
        if (page.status === 'awaiting_human' || page.status === 'cancelled') return;

        const s = sink.state;
        track('ai.message.received', {
          ...analyticsContext,
          transport: 'run',
          duration_ms: Date.now() - startedAt,
          tools_used: s.toolsUsed.length,
          has_chart: Boolean(s.chart),
          has_pending_action: s.pendingActions.length > 0,
          path: 'run',
        });
        onDone?.({
          fullText: s.fullText,
          toolsUsed: s.toolsUsed,
          agent: s.agentMeta,
          chart: s.chart,
          groundingWarning: s.groundingWarning,
          pendingActions: s.pendingActions,
          pendingClarifications: s.pendingClarifications,
          path: 'run',
          runId,
          truncated: false,
        });
        return;
      }

      // Salida por bandera de aborto: el usuario paró de mirar. No es un error
      // y el run sigue su curso en el servidor.
      if (!isCurrent()) { clearSlowToast(); return; }

      fail({ isTimeout: true, message: i18n.t('ai.run.timeout', 'La consulta está tardando demasiado. Vuelve a intentarlo.'), retryable: true }, 'watch_timeout');
    } catch (err) {
      fail({ isTimeout: false, message: err?.message, status: err?.status, retryable: err?.status === 503 }, err?.status || 'unknown');
    } finally {
      clearSlowToast();
      ctl.controller = null;
      if (ctlRef.current === ctl) setIsRunning(false);
    }
  }, []);

  return { run, abort, isRunning };
}
